import { LineLayer, PointLayer } from '@antv/l7'
import type { ILayer, Scene } from '@antv/l7'
import { useEffect, useRef } from 'react'
import { buildTrackRender, type TrackTask } from '../lib/track'

type TrackLayerEntry = { line: ILayer; arrows: ILayer }

/**
 * 任务轨迹图层管理：每个任务一组（渐变折线 + 方向箭头），
 * 任务集合变化时增量 addLayer/setData/removeLayer，不重建 Scene。
 * 供救援流向面板内嵌地图与指挥中心主地图共用。
 */
export function useTrackLayers(
  sceneRef: { current: Scene | null },
  ready: boolean,
  tasks: TrackTask[],
  opts?: { zIndex?: number; fitBounds?: boolean },
) {
  const layersRef = useRef<Map<string, TrackLayerEntry>>(new Map())
  const fittedRef = useRef('')
  const zIndex = opts?.zIndex ?? 6
  const fitBounds = opts?.fitBounds ?? false

  useEffect(() => {
    const scene = sceneRef.current
    if (!scene || !ready) return

    const alive = new Set<string>()
    let minLon = Infinity
    let minLat = Infinity
    let maxLon = -Infinity
    let maxLat = -Infinity

    for (const task of tasks) {
      const render = buildTrackRender(task)
      if (!render) continue
      alive.add(task.taskId)
      let entry = layersRef.current.get(task.taskId)
      if (!entry) {
        const line = new LineLayer({ zIndex })
          .source([], { parser: { type: 'json', x: 'lon', y: 'lat' } })
          .shape('line')
          .size(2.5)
          .color('color', (c: string) => c)
          .style({ opacity: 0.95 })
        const arrows = new PointLayer({ zIndex: zIndex + 1 })
          .source([], { parser: { type: 'json', x: 'lon', y: 'lat' } })
          .shape('triangle')
          .size(7)
          .color('color', (c: string) => c)
          .rotate('heading')
          .style({ stroke: '#070b12', strokeWidth: 1 })
        scene.addLayer(line)
        scene.addLayer(arrows)
        entry = { line, arrows }
        layersRef.current.set(task.taskId, entry)
      }
      entry.line.setData(render.points)
      entry.arrows.setData(render.arrows)
      for (const p of render.points) {
        minLon = Math.min(minLon, p.lon)
        maxLon = Math.max(maxLon, p.lon)
        minLat = Math.min(minLat, p.lat)
        maxLat = Math.max(maxLat, p.lat)
      }
    }

    // 清理已消失任务（轮询数据更新 / 权限变化 / 图层关闭）
    for (const [taskId, entry] of layersRef.current) {
      if (!alive.has(taskId)) {
        void scene.removeLayer(entry.line)
        void scene.removeLayer(entry.arrows)
        layersRef.current.delete(taskId)
      }
    }

    // 任务集合变化时自适应一次视野（小地图用，主地图不抢占视野）
    if (fitBounds && alive.size > 0) {
      const key = [...alive].sort().join(',')
      if (key !== fittedRef.current && Number.isFinite(minLon)) {
        fittedRef.current = key
        scene.fitBounds([
          [minLon, minLat],
          [maxLon, maxLat],
        ])
      }
    }
    if (alive.size === 0) fittedRef.current = ''
  }, [sceneRef, ready, tasks, zIndex, fitBounds])

  // 卸载时清理图层
  useEffect(
    () => () => {
      const scene = sceneRef.current
      if (!scene) return
      for (const [, entry] of layersRef.current) {
        void scene.removeLayer(entry.line)
        void scene.removeLayer(entry.arrows)
      }
      layersRef.current.clear()
    },
    [sceneRef],
  )
}
